import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_KEYS, API_SERVICE } from '../api';
import type { Requisition } from '../types';

interface UseRequisitionGroupDataParams {
  requisition?: Requisition | null;
  isOpen?: boolean;
}

export const useRequisitionGroupData = ({
  requisition,
  isOpen = true,
}: UseRequisitionGroupDataParams) => {
  // Determine if requisition is part of a group
  const isPartOfGroup = useMemo(() => {
    if (!requisition) return false;
    return !!requisition.requisition_group_id;
  }, [requisition]);

  // Fetch all requisitions to get group data - only when part of a group
  const { data: allRequisitionsData, isFetching: isLoadingRequisitions } =
    useQuery({
      queryKey: [
        API_KEYS.getRequisitions,
        { page: 1, limit: 200, sort_key: 'created_at', sort_order: 'desc' },
      ],
      queryFn: () =>
        API_SERVICE.getRequisitions({
          page: 1,
          limit: 200,
          sort_key: 'created_at',
          sort_order: 'desc',
        }),
      enabled: isPartOfGroup && isOpen,
    });

  // Get all group requisitions
  const allGroupRequisitions = useMemo(() => {
    if (!isPartOfGroup || !requisition?.requisition_group_id) return [];

    const allReqs = allRequisitionsData?.requisitions || [];
    const groupReqs = allReqs.filter(
      (req: Requisition) =>
        req.requisition_group_id === requisition.requisition_group_id &&
        req.requisition_group_id != null
    );

    // Ensure current requisition is in the list
    const currentReqInList = groupReqs.some((req) => req.id === requisition.id);
    if (!currentReqInList && requisition.requisition_group_id != null) {
      groupReqs.push(requisition);
    }

    return groupReqs;
  }, [isPartOfGroup, requisition, allRequisitionsData]);

  return {
    isPartOfGroup,
    availableRequisitions: allRequisitionsData?.requisitions || [],
    allGroupRequisitions,
    isLoadingRequisitions,
  };
};
